import { Box, Button, TextField, Typography, Paper, CircularProgress } from '@mui/material';
import { ChevronLeft, ChevronRight, Mic } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import {
  transcribeAudio as transcribeAudioAction,
  setTranscript,
  transcribeToPlan as transcribeToPlanAction,
} from '../../../../../redux/slices/treatmentPlan';

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const WAVEFORM_BARS = 42;

export default function CreateWithAiOptions() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStart, setRecordingStart] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(Array(WAVEFORM_BARS).fill(0));
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { transcription, transcribeToPlanStatus } = useSelector(
    (state: RootState) => state.treatmentPlan
  );

  useEffect(() => {
    if (transcribeToPlanStatus.success) {
      navigate('/dashboard/treatment-plan');
    }
  }, [transcribeToPlanStatus.success, navigate]);

  // Timer for recording duration
  useEffect(() => {
    if (isRecording && recordingStart !== null) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - recordingStart) / 1000));
      }, 500);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, recordingStart]);

  // Waveform animation
  useEffect(() => {
    if (!isRecording) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setWaveform(Array(WAVEFORM_BARS).fill(0));
      return;
    }
    function draw() {
      if (analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        // Split into bars
        const arr = Array.from(dataArrayRef.current);
        const barSize = Math.floor(arr.length / WAVEFORM_BARS);
        const bars = Array(WAVEFORM_BARS)
          .fill(0)
          .map((_, i) => {
            const slice = arr.slice(i * barSize, (i + 1) * barSize);
            return slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : 0;
          });
        setWaveform(bars);
      }
      animationRef.current = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isRecording]);

  // Start recording
  const handleDictate = async () => {
    setIsRecording(true);
    setRecordingStart(Date.now());
    setWaveform(Array(WAVEFORM_BARS).fill(0));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let AudioCtx: typeof AudioContext;
      if (typeof window !== 'undefined' && 'webkitAudioContext' in window) {
        AudioCtx = (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext!;
      } else {
        AudioCtx = window.AudioContext;
      }
      audioContextRef.current = new AudioCtx();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 128;
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
      sourceRef.current.connect(analyserRef.current);
      let mimeType = '';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else {
        mimeType = '';
      }
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType });
        // Clean up audio context
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        if (sourceRef.current) sourceRef.current.disconnect();
        if (analyserRef.current) analyserRef.current.disconnect();
        // Dispatch transcription API call
        dispatch(transcribeAudioAction(blob));
      };
      recorder.start();
    } catch {
      setIsRecording(false);
      setRecordingStart(null);
      alert('Could not access microphone.');
    }
  };

  // Stop recording
  const handleStop = () => {
    setIsRecording(false);
    setRecordingStart(null);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (sourceRef.current && sourceRef.current.mediaStream) {
      sourceRef.current.mediaStream.getTracks().forEach(track => track.stop());
    }
  };

  // Handle manual transcript edit
  const handleTranscriptChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setTranscript(e.target.value));
  };

  // Handle Generate Plan
  const handleGeneratePlan = () => {
    if (transcription.transcript) {
      dispatch(transcribeToPlanAction(transcription.transcript));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#FAFBFC', p: '32px' }}>
      <Box py={'32px'} pt="0">
        <Button
          variant="text"
          color="primary"
          sx={{ minWidth: 100 }}
          startIcon={<ChevronLeft />}
          onClick={() => navigate('/dashboard/treatment-plan/select-patient')}
        >
          Create options
        </Button>
      </Box>
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        {/* Ambient listening option */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
            p: 2,
            borderRadius: 2,
            boxShadow: 'none',
            borderColor: '#D1D5DB',
            cursor: 'pointer',
          }}
        >
          <Mic color="#5D5DF7" style={{ marginRight: 16 }} />
          <Typography fontWeight={500} fontSize={18} color="#222">
            Start ambient listening (scribe)
          </Typography>
        </Paper>

        {/* Generate with clinical notes/instructions */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            boxShadow: 'none',
            borderColor: '#D1D5DB',
            p: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #E5E7EB' }}
          >
            <img
              src="/sparkles.svg"
              alt="Sparkles"
              style={{ width: 24, height: 24, marginRight: 12 }}
            />
            <Typography fontWeight={600} fontSize={18} color="#222">
              Generate with clinical notes / Instructions
            </Typography>
          </Box>
          <Box sx={{ p: 3, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography color="#666" fontSize={15} sx={{ flex: 1 }}>
                Bilby will use this information to generate a structured treatment plan for your
                patient.
              </Typography>
              {isRecording ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Mic size={18} color="#F04438" fill="#F04438" />}
                  sx={{
                    ml: 2,
                    borderColor: '#F04438',
                    color: '#F04438',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                  }}
                  onClick={handleStop}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Mic size={18} color="#5D5DF7" />}
                  sx={{
                    ml: 2,
                    borderColor: '#D1D5DB',
                    color: '#5D5DF7',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                  }}
                  onClick={handleDictate}
                  disabled={transcription.loading}
                >
                  Dictate
                </Button>
              )}
            </Box>
            {/* Recording/Transcribing UI */}
            {isRecording && !transcription.loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  pl: 0.5,
                }}
              >
                <Box display="flex" alignItems="center">
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#F04438',
                      marginRight: 8,
                    }}
                  />
                  <Typography color="#F04438" fontWeight={500} fontSize={15} sx={{ mr: 2 }}>
                    Recording in progress &bull; {formatTime(elapsed)}
                  </Typography>
                </Box>
                {/* Waveform visualization */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 32, ml: 1 }}>
                  {waveform.map((v, i) => (
                    <div
                      key={i}
                      style={{
                        width: 4,
                        height: `${8 + (v / 255) * 24}px`,
                        background: '#5D5DF7',
                        marginRight: 2,
                        borderRadius: 2,
                        transition: 'height 0.1s',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {/* Transcription loading UI */}
            {!isRecording && transcription.loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 0.5 }}>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                <Typography fontSize={15}>Transcribing...</Typography>
              </Box>
            )}
            {transcription.error && (
              <Typography color="error" fontSize={14} mb={1}>
                {transcription.error}
              </Typography>
            )}
            <TextField
              multiline
              minRows={5}
              maxRows={15}
              fullWidth
              placeholder="Enter clinical notes and detailed instructions..."
              value={transcription.transcript}
              onChange={handleTranscriptChange}
              sx={{
                background: '#fff',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                mb: 3,
                '& .MuiInputBase-root': {
                  maxHeight: '400px',
                  overflow: 'auto',
                },
              }}
              InputProps={{
                style: { fontSize: 16 },
              }}
              disabled={transcription.loading}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                boxShadow: 'none',
              }}
              endIcon={<ChevronRight size={18} />}
              onClick={handleGeneratePlan}
              disabled={transcribeToPlanStatus.loading || !transcription.transcript}
            >
              {transcribeToPlanStatus.loading ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : null}
              Generate plan
            </Button>
            {transcribeToPlanStatus.error && (
              <Typography color="error" fontSize={14} mt={1}>
                {transcribeToPlanStatus.error}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
